/* =========================================================================
   J+I virtual ring sizer — assets/ji-ring-sizer.js
   Loaded only by sections/ji-ring-sizer.liquid.

   Browsers can't report physical screen size, so the customer calibrates
   once against a known object (a card's 53.98 mm short edge, or a US
   quarter). That gives CSS px per mm, which draws a true-size ring circle.
   ========================================================================= */
(() => {
  if (customElements.get('ji-ring-sizer')) return;

  const CARD_W_MM = 85.6;    // ISO/IEC 7810 ID-1 card, shown sideways
  const CARD_H_MM = 53.98;   // the edge customers actually match
  const CARD_R_MM = 3.18;
  const QUARTER_MM = 24.26;

  const SIZE_MIN = 5;
  const SIZE_MAX = 13;
  const SCALE_MIN = 3;       // CSS px per mm (keep in sync with range markup)
  const SCALE_MAX = 9;
  const DIAM_MIN = 13;       // mm
  const DIAM_MAX = 24;
  const SCALE_STEP = 0.01;   // ≈0.5 px at the card's bottom edge per tap
  const DIAM_STEP = 0.05;    // mm per tap
  const STORE_KEY = 'ji-ring-sizer:v1';
  const RETURN_KEY = 'ji-ring-sizer:return';

  // US ring size ↔ inside diameter (mm). Matches the printed guide's chart.
  const diamForSize = (s) => 11.63 + 0.8128 * s;
  const sizeForDiam = (d) => (d - 11.63) / 0.8128;
  const fmtSize = (s) => (Number.isInteger(s) ? String(s) : s.toFixed(1));
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const mq = (q) => window.matchMedia(q).matches;
  const isDesktop = () => mq('(hover: hover) and (pointer: fine) and (min-width: 1000px)');

  const screenSignature = () => {
    const w = window.screen.width;
    const h = window.screen.height;
    return `${Math.min(w, h)}x${Math.max(w, h)}@${window.devicePixelRatio || 1}`;
  };

  class JiRingSizer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.$ = (sel) => this.querySelector(sel);
      this.$$ = (sel) => this.querySelectorAll(sel);

      this.focusDialog = this.$('[data-focus]');
      this.qrDialog = this.$('[data-qr-dialog]');
      this.calRange = this.$('[data-range="cal"]');
      this.measureRange = this.$('[data-range="measure"]');

      this.bandNote = this.dataset.bandNote || '';
      this.ref = 'card';
      this.scale = mq('(pointer: coarse)') ? 6.2 : 96 / 25.4; // first guess only
      this.diam = diamForSize(7);

      const saved = this.loadScale();
      this.calibrated = saved !== null;
      if (this.calibrated) this.scale = saved;

      this.bindRanges();
      this.bindNudges();
      this.bindActions();
      this.bindDialogs();
      this.watchZoom();
      this.detectReturn();
      this.render();

      if (isDesktop()) {
        this.openQr();
      } else if (new URLSearchParams(window.location.search).get('start') === '1') {
        this.open();
      }
    }

    /* ---------- focused mode ---------- */

    open() {
      if (!this.focusDialog || this.focusDialog.open) return;
      if (this.qrDialog && this.qrDialog.open) this.qrDialog.close();

      this.focusDialog.showModal();
      document.documentElement.classList.add('ji-rs-lock');
      // A history entry lets the phone's back gesture close focused mode
      // instead of leaving the page.
      window.history.pushState({ jiRs: true }, '');
      this.pushed = true;
      this.show(this.calibrated ? 'measure' : 'calibrate');
    }

    requestClose() {
      if (this.pushed && window.history.state && window.history.state.jiRs) {
        window.history.back(); // popstate handler finishes closing
      } else {
        this.hide();
      }
    }

    hide() {
      this.pushed = false;
      if (this.focusDialog.open) this.focusDialog.close();
      document.documentElement.classList.remove('ji-rs-lock');
      const start = this.$('[data-action="start"]');
      if (start) start.focus({ preventScroll: true });
    }

    show(step) {
      this.$$('[data-step]').forEach((s) => { s.hidden = s.dataset.step !== step; });
      this.dataset.current = step;
      this.render();
      const screen = this.$(`[data-step="${step}"]`);
      const heading = [...screen.querySelectorAll('[data-step-heading]')].find((h) => h.offsetParent !== null);
      if (heading) heading.focus({ preventScroll: true });
    }

    /* ---------- input ---------- */

    bindRanges() {
      this.calRange.addEventListener('input', () => {
        this.scale = Number(this.calRange.value);
        this.render();
      });
      this.measureRange.addEventListener('input', () => {
        this.diam = Number(this.measureRange.value);
        this.render();
      });
    }

    bindNudges() {
      this.$$('[data-nudge]').forEach((btn) => {
        const target = btn.dataset.nudge;
        const dir = Number(btn.dataset.dir);
        const step = () => this.nudge(target, dir);
        let delay;
        let repeat;
        const stop = () => {
          clearTimeout(delay);
          clearInterval(repeat);
        };

        // Tap = one fine step; press and hold repeats.
        btn.addEventListener('pointerdown', (e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          step();
          delay = setTimeout(() => { repeat = setInterval(step, 70); }, 380);
        });
        ['pointerup', 'pointerleave', 'pointercancel'].forEach((t) => btn.addEventListener(t, stop));
        btn.addEventListener('contextmenu', (e) => e.preventDefault());
        // Keyboard (Enter/Space) fires click with detail 0.
        btn.addEventListener('click', (e) => { if (e.detail === 0) step(); });
      });
    }

    nudge(target, dir) {
      if (target === 'cal') {
        this.scale = clamp(this.scale + dir * SCALE_STEP, SCALE_MIN, SCALE_MAX);
      } else {
        this.diam = clamp(this.diam + dir * DIAM_STEP, DIAM_MIN, DIAM_MAX);
      }
      this.render();
    }

    bindActions() {
      this.addEventListener('click', (e) => {
        const el = e.target.closest('[data-action]');
        if (!el || !this.contains(el)) return;

        switch (el.dataset.action) {
          case 'start': this.open(); break;
          case 'close': this.requestClose(); break;
          case 'calibrated':
            this.saveScale();
            this.calibrated = true;
            this.show('measure');
            break;
          case 'toggle-ref':
            this.ref = this.ref === 'card' ? 'quarter' : 'card';
            this.render();
            break;
          case 'recalibrate': this.show('calibrate'); break;
          case 'see-size': this.showResult(); break;
          case 'measure-again': this.show('measure'); break;
          case 'open-qr': this.openQr(); break;
          case 'close-qr': if (this.qrDialog) this.qrDialog.close(); break;
          default:
        }
      });
    }

    bindDialogs() {
      // Esc key: route through requestClose so history stays in step.
      this.focusDialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.requestClose();
      });
      window.addEventListener('popstate', () => {
        if (this.focusDialog.open) this.hide();
      });
      if (this.qrDialog) {
        // Clicking the backdrop closes the QR popup (the dialog has no padding).
        this.qrDialog.addEventListener('click', (e) => {
          if (e.target === this.qrDialog) this.qrDialog.close();
        });
      }
    }

    openQr() {
      if (!this.qrDialog || typeof this.qrDialog.showModal !== 'function' || this.qrDialog.open) return;
      this.qrDialog.showModal();
    }

    /* ---------- drawing ---------- */

    render() {
      const px = this.scale;
      const isCard = this.ref === 'card';

      const card = this.$('[data-card]');
      const coin = this.$('[data-coin]');
      card.hidden = !isCard;
      coin.hidden = isCard;
      this.$('[data-coin-copy]').hidden = isCard;
      card.style.width = `${CARD_W_MM * px}px`;
      card.style.height = `${CARD_H_MM * px}px`;
      card.style.borderRadius = `${CARD_R_MM * px}px`;
      coin.style.width = coin.style.height = `${QUARTER_MM * px}px`;
      this.$('[data-action="toggle-ref"]').textContent = isCard ? 'Use a quarter' : 'Use a card';

      this.calRange.value = px;
      this.calRange.setAttribute('aria-valuetext', `${Math.round(((px - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100)} percent`);

      const gauge = this.$('[data-gauge]');
      gauge.style.width = gauge.style.height = `${this.diam * px}px`;

      const label = this.liveLabel();
      this.$('[data-live-size]').textContent = label;
      this.$('[data-live-mm]').textContent = `${this.diam.toFixed(1)} mm inside`;
      this.measureRange.value = this.diam;
      this.measureRange.setAttribute('aria-valuetext', `${label}, ${this.diam.toFixed(1)} millimeters`);
    }

    liveLabel() {
      const raw = sizeForDiam(this.diam);
      if (raw < SIZE_MIN - 0.25) return `Smaller than ${SIZE_MIN}`;
      if (raw > SIZE_MAX + 0.25) return `Larger than ${SIZE_MAX}`;
      return `About size ${fmtSize(clamp(Math.round(raw * 2) / 2, SIZE_MIN, SIZE_MAX))}`;
    }

    showResult() {
      const raw = sizeForDiam(this.diam);
      const outOfRange = raw < SIZE_MIN - 0.25 || raw > SIZE_MAX + 0.25;
      this.$('[data-result-main]').hidden = outOfRange;
      this.$('[data-result-out]').hidden = !outOfRange;

      if (outOfRange) {
        this.$('[data-result-out-text]').textContent = raw < SIZE_MIN
          ? `This ring measures smaller than a size ${SIZE_MIN}, the smallest we make.`
          : `This ring measures larger than a size ${SIZE_MAX}, the largest we make.`;
      } else {
        const size = clamp(Math.round(raw * 2) / 2, SIZE_MIN, SIZE_MAX);
        this.$('[data-result-size]').textContent = fmtSize(size);
        this.$('[data-result-mm]').textContent = `${this.diam.toFixed(1)} mm inside diameter`;

        // Close to a quarter size: explain the two neighbours.
        const halfSteps = raw * 2;
        const lower = Math.floor(halfSteps) / 2;
        const upper = lower + 0.5;
        const nearQuarter = Math.abs(halfSteps - Math.round(halfSteps)) > 0.3;
        const between = this.$('[data-result-between]');
        between.hidden = !(nearQuarter && lower >= SIZE_MIN && upper <= SIZE_MAX);
        between.textContent = `Your ring falls between ${fmtSize(lower)} and ${fmtSize(upper)}. For a snug fit choose ${fmtSize(lower)}; for a looser fit choose ${fmtSize(upper)}.`;

        const up = size + 0.5;
        this.$('[data-result-band]').textContent = this.bandNote
          + (up <= SIZE_MAX ? ` For you, that's a size ${fmtSize(up)}.` : '');
      }

      this.show('result');
    }

    /* ---------- back to the product they came from ---------- */

    detectReturn() {
      let url = null;
      try {
        const ref = document.referrer ? new URL(document.referrer) : null;
        const sameSite = ref && ref.origin === window.location.origin;
        if (sameSite && /\/products\/(?!ring-sizer\b)[^/?#]+/.test(ref.pathname)) {
          url = ref.pathname + ref.search; // keeps ?variant=
          window.sessionStorage.setItem(RETURN_KEY, url);
        } else if (sameSite && ref.pathname !== window.location.pathname) {
          // Arrived from elsewhere on the site: forget any earlier product.
          window.sessionStorage.removeItem(RETURN_KEY);
        } else {
          // Reload, QR scan, or no referrer: reuse this session's product.
          url = window.sessionStorage.getItem(RETURN_KEY);
        }
      } catch (e) { /* storage or URL unavailable */ }
      if (url) this.applyReturn(url);
    }

    applyReturn(url, title) {
      const link = this.$('[data-return-link]');
      if (!link) return;
      link.href = url;
      link.hidden = false;
      const shopButton = this.$('[data-shop-button]');
      const shopLink = this.$('[data-shop-link]');
      if (shopButton) shopButton.hidden = true;
      if (shopLink) shopLink.hidden = false;

      if (title) {
        link.textContent = `Back to ${title}`;
        return;
      }
      // Product title from Shopify's storefront JSON; keeps the fallback label if it fails.
      const match = url.match(/\/products\/([^/?#]+)/);
      if (!match) return;
      fetch(`/products/${match[1]}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => { if (p && p.title) link.textContent = `Back to ${p.title}`; })
        .catch(() => {});
    }

    /* ---------- pinch-zoom guard ---------- */

    watchZoom() {
      const vv = window.visualViewport;
      if (!vv) return;
      const warn = this.$('[data-zoom-warning]');
      const check = () => { warn.hidden = Math.abs(vv.scale - 1) < 0.02; };
      vv.addEventListener('resize', check);
      check();
    }

    /* ---------- saved calibration (per device) ---------- */

    loadScale() {
      try {
        const data = JSON.parse(window.localStorage.getItem(STORE_KEY));
        if (data && data.sig === screenSignature() && data.scale >= SCALE_MIN && data.scale <= SCALE_MAX) {
          return data.scale;
        }
      } catch (e) { /* storage unavailable */ }
      return null;
    }

    saveScale() {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify({
          scale: this.scale,
          ref: this.ref,
          sig: screenSignature(),
          at: Date.now(),
        }));
      } catch (e) { /* storage unavailable */ }
    }
  }

  customElements.define('ji-ring-sizer', JiRingSizer);
})();